//#region Imports Packages and Libs

import { Util } from "./../../../util";
import { CLICK_ACTION_POSTBACK, MESSAGE_COMPONENT_TYPE, STORE_MUTATION } from "../../../enums";
import { MessageStructureComponent, InlineClickAction } from "../../../types";
import { store } from "../../../store/store";
import { ChatService } from "../../../service/chat_service";
import { addUserMessage } from "../../addUserMessage";

//#endregion

//#region PostbackHandler Class

/**
 * Handles postback actions triggered by inline UI elements in the chat interface.
 *
 * The `PostbackHandler` class encapsulates logic for processing different types of postback actions,
 * such as clearing the chat screen, replying with a user message, or handling generic postbacks.
 * It interacts with the application store to update UI state and uses the `ChatService` to send messages.
 */
export class PostbackHandler {
	//#region Private Methods

	/**
	 * Handles the "clear screen" postback action by resetting relevant UI states and sending a message.
	 *
	 * @param hasResponse - Indicates whether a response has been received and the action should proceed.
	 * @param data - The message structure component to be sent via the chat service.
	 *
	 * @remarks
	 * - Commits mutations to update the request/response state and show the typing indicator.
	 * - Sends the provided message data using a new instance of `ChatService`.
	 */
	static handleClearScreenPostback(hasResponse: boolean, data: MessageStructureComponent): void {
		try {
			if (!hasResponse) return;
			store.commit(STORE_MUTATION.SetRequestResponseReceived, false);
			store.commit(STORE_MUTATION.SetShowTypingIndicator, true);
			const service = new ChatService();
			service.sendMessage(data);
		} catch {
			//return null;
		}
	}

	/**
	 * Handles the reply postback action by committing a mutation to the store indicating
	 * that a request response has been received, and adds the user's message to the chat.
	 *
	 * @param msg - The message string to be added as a user message.
	 * @param data - The message structure component containing additional message data.
	 */
	static handleReplyPostback(msg: string, data: MessageStructureComponent): void {
		try {
			store.commit(STORE_MUTATION.SetRequestResponseReceived, true);
			addUserMessage(msg, false, false, data);
		} catch {
			//return null;
		}
	}

	/**
	 * Handles a generic postback action by sending a message if a response is expected.
	 *
	 * @param hasResponse - Indicates whether a response is expected for the postback action.
	 * @param data - The message structure component containing the data to be sent.
	 */
	static handleGenericPostback(hasResponse: boolean, data: MessageStructureComponent): void {
		try {
			if (!hasResponse) return;
			store.commit(STORE_MUTATION.SetRequestResponseReceived, false);
			const service = new ChatService();
			service.sendMessage(data);
		} catch {
			//return null;
		}
	}

	/**
	 * Validates whether the provided data is a non-null object suitable for further processing.
	 *
	 * @param data - The data object to validate, expected to conform to the `MessageStructureComponent` type.
	 * @returns `true` if the data is a valid object; otherwise, `false`.
	 */
	static isValidData(data: MessageStructureComponent, action: unknown): boolean {
		if (!data || typeof data !== "object") {
			console.error("[postback_handler.ts] Invalid data object passed to execute method.");
			console.log({ data, action });
			return false;
		}
		return true;
	}

	/**
	 * This method sets the `content` property to `null` and the `type` property to `"inline_click_action"`.
	 * If the `clickAction` property is not already defined or is `null`, it initializes it with the provided
	 * `action` object and sets its `settings` property to `null`.
	 *
	 * @param data - The message structure component to be prepared for the action.
	 * @param action - The inline click action to associate with the message component.
	 */
	static prepareDataForAction(data: MessageStructureComponent, action: InlineClickAction): void {
		try {
			// Set default structure values for content and type
			data.content = null;
			data.type = MESSAGE_COMPONENT_TYPE.InlineClickAction;

			// Check if the clickAction is already defined in the data
			// If not, create a new clickAction object with the action and settings
			if (!("clickAction" in data) || data.clickAction == null) {
				data.clickAction = { ...action, settings: null };
			}
		} catch (ex) {
			//console.error("[postback_handler.ts] Error in prepareDataForAction:", ex);
		}
	}

	//#endregion

	//#region Public Methods

	/**
	 * Executes the appropriate postback action based on the provided `InlineClickAction` and message data.
	 *
	 * This method determines the type of postback action to perform:
	 * - If the action is a "ClearScreen" postback, it calls `handleClearScreenPostback`.
	 * - If the action contains reply text, it calls `handleReplyPostback`.
	 * - Otherwise, it calls `handleGenericPostback`.
	 *
	 * Any errors encountered during execution are caught and logged to the console.
	 *
	 * @param action - The inline click action containing postback information.
	 * @param data - The message structure component associated with the action.
	 */
	static execute(action: InlineClickAction, data: MessageStructureComponent): void {
		try {
			// Guard clause: Ensure `data` is a valid object
			if (!this.isValidData(data, action)) return;

			const message = action.replyText || "";
			const hasReplyText = !Util.isNullOrEmpty(message) && Util.trimString(message) !== "";
			const isClearScreen = action.postback === CLICK_ACTION_POSTBACK.ClearScreen;
			const hasResponse = store.state.requestResponseReceived;

			// Set default structure values for content and type
			this.prepareDataForAction(data, action);

			// If postback action is clear screen, handle it
			if (isClearScreen) {
				this.handleClearScreenPostback(hasResponse, data);
				return;
			}

			// If postback action is reply, handle it
			if (hasReplyText) {
				this.handleReplyPostback(message, data);
				return;
			}

			// Finally, handle generic postback action
			this.handleGenericPostback(hasResponse, data);
		} catch (ex) {
			console.error("[postback_handler.ts] An error has occurred \n-> Inside execute -> Error: ", ex);
		}
	}

	//#endregion
}

//#endregion
